import { TileMapMetaProvider } from "./tile_map_meta_provider"
import { mergeCollinearSegments, Vector2D, VectorLine } from "./ray_casting"
import { Bounds, Region, TileMapBase, TileSize } from "./models"
import { autotile } from "./room/autotiler"
import { blankMap } from "./util"

// Which lpc-terrain platform variants leave part of their square transparent, measured off
// the sheet itself: the rock starts half a square in on that side. The south edge and the
// cliff face below it, the interior fill and the thin-wall caps all paint the whole square.
import { CaveArtSetBackTop, CaveArtSetBackLeft, CaveArtSetBackRight } from "./tile_blocking_geometry"

export interface PolymapMeta {
    vectorLines: Array<VectorLine>
    autotileMap: TileMapBase<number>
}
export const buildPolyMap = (
    tilemapMetaProvider: TileMapMetaProvider,
    { insetWalls = false }: { insetWalls?: boolean } = {},
): PolymapMeta => {
    const { isOcclusionAt } = tilemapMetaProvider
    let polyMap: Array<VectorLine> = []

    polyMap[0] = polyMap[1] = polyMap[2] = polyMap[3] = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 } }

    const occlusionMap = blankMap(tilemapMetaProvider.width(), tilemapMetaProvider.height())
    const blockMap = blankMap(tilemapMetaProvider.width(), tilemapMetaProvider.height())

    for (let x = 0; x < tilemapMetaProvider.width(); x++) {
        for (let y = 0; y < tilemapMetaProvider.height(); y++) {
            occlusionMap[y][x] = isOcclusionAt(x, y) ? 1 : 0
            blockMap[y][x] = occlusionMap[y][x] && (insetWalls || tilemapMetaProvider.getEntityTileAt(x, y)) ? 1 : 0
        }
    }
    const blockingAuto = autotile(occlusionMap)
    // Outdoor blocks pull every exposed face back to the tile's midline so the near half of
    // the block stays lit.
    const inset = TileSize / 2 - 0.5
    const blockBounds = (x: number, y: number) => {
        if (!blockMap[y]?.[x]) return undefined
        return {
            left: x * TileSize + (blockMap[y][x - 1] ? 0 : inset),
            right: (x + 1) * TileSize - (blockMap[y][x + 1] ? 0 : inset),
            top: y * TileSize + (blockMap[y - 1]?.[x] ? 0 : inset),
            bottom: (y + 1) * TileSize - (blockMap[y + 1]?.[x] ? 0 : inset),
        }
    }

    // A cave wall occludes along its DRAWN surface, not its square's boundary. The generated
    // cave's platform art lives in map5, and the variants listed in CaveArtSetBack* leave the
    // far half of their square transparent so the rock reads as a face with cave floor in
    // front of it. An occluder on the square's boundary makes the reveal light those
    // transparent pixels, and the wall reads as solid black however close you stand to it.
    // Which side is set back is a property of the ARTWORK, so it is read off the tile that
    // was actually placed -- a wall one square thin still uses a drawn-back edge tile, so it
    // cannot be inferred from the shape of the rock around it.
    const caveWalls = insetWalls ? tilemapMetaProvider.resolveTileMapFromLayerId("map5") : undefined
    const terrain = insetWalls ? tilemapMetaProvider.getTilesetById("lpc-terrain") : undefined
    const surfaceInset = TileSize / 2
    const caveArt = (x: number, y: number) => {
        const gid = caveWalls?.[x]?.[y]
        // A deposit's sprite fills its whole square, so it is never set back.
        if (!gid || !terrain || tilemapMetaProvider.getEntityTileAt(x, y)) return undefined
        return Number(gid) - terrain.firstGid
    }
    const caveBounds = (x: number, y: number) => {
        if (!blockMap[y]?.[x]) return undefined
        const art = caveArt(x, y)
        // Only a side fronting open ground is set back, so two neighboring occluders still
        // meet exactly on the side they share and the outline around the rock stays closed.
        // No variant is set back on BOTH its left and its right, so an occluder is always at
        // least half a tile across -- which is what the first version of this got wrong. It
        // set back all four faces, so an exposed square shrank to a one-pixel speck that a
        // steep ray sailed straight past, and once a ray was inside the rock nothing stopped
        // it (interior squares have no faces at all): it tunnelled through the whole mass and
        // lit the floor far beyond as a long wedge.
        const back = (open: boolean, setBack: Set<number>) =>
            open && art !== undefined && setBack.has(art) ? surfaceInset : 0
        const openAt = (atX: number, atY: number) => !blockMap[atY]?.[atX]
        return {
            left: x * TileSize + back(openAt(x - 1, y), CaveArtSetBackLeft),
            right: (x + 1) * TileSize - back(openAt(x + 1, y), CaveArtSetBackRight),
            top: y * TileSize + back(openAt(x, y - 1), CaveArtSetBackTop),
            bottom: (y + 1) * TileSize,
        }
    }

    const face = (from: number, to: number, at: number, horizontal: boolean) =>
        polyMap.push(horizontal
            ? { a: { x: from, y: at }, b: { x: to, y: at } }
            : { a: { x: at, y: from }, b: { x: at, y: to } })

    const outline = (from: number, to: number, at: number, horizontal: boolean, neighborFrom?: number, neighborTo?: number) => {
        const add = (start: number, end: number) => {
            if (end <= start) return
            face(start, end, at, horizontal)
        }
        if (neighborFrom === undefined) add(from, to)
        else {
            // Remove the shared part only. The remaining spans close concave corners.
            add(from, Math.min(to, neighborFrom))
            add(Math.max(from, neighborTo), to)
        }
    }

    const evaluteAutotile = (x: number, y: number, autoId: number): Array<VectorLine> => {
        const polyMap: Array<VectorLine> = []
        const o: Vector2D = { x: x * TileSize, y: y * TileSize }
        const offset = TileSize / 2

        if (autoId === 18) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 8) {
            polyMap.push({
                a: { x: o.x, y: o.y - offset + TileSize },
                b: { x: o.x + TileSize, y: o.y - offset + TileSize },
            })
        }
        if (autoId === 14) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }
        if (autoId === 15) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + offset, y: o.y + offset },
            })
        }

        if (autoId === 6) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y - offset },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 3) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y - offset },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x - offset, y: o.y + offset },
            })
        }

        if (autoId === 5) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 2) {
            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + offset, y: o.y + offset },
            })
        }

        if (autoId === 13) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }
        if (autoId === 1) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }

        if (autoId === 19) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }

        if (autoId === 21) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }

        return polyMap
    }

    for (let x = 0; x < tilemapMetaProvider.width(); x++) {
        for (let y = 0; y < tilemapMetaProvider.height(); y++) {
            if (blockMap[y][x]) {
                // Trace the outline of the solid region, dropping the span each face shares
                // with its neighbor. Because neighboring bounds always meet exactly on a
                // shared side, what is left is a closed curve: no sightline can get inside it.
                const bounds = insetWalls ? caveBounds : blockBounds
                const { left, right, top, bottom } = bounds(x, y)
                const above = bounds(x, y - 1)
                const below = bounds(x, y + 1)
                const west = bounds(x - 1, y)
                const east = bounds(x + 1, y)
                outline(left, right, top, true, above?.left, above?.right)
                outline(left, right, bottom, true, below?.left, below?.right)
                outline(top, bottom, left, false, west?.top, west?.bottom)
                outline(top, bottom, right, false, east?.top, east?.bottom)
                if (insetWalls) continue
                // Outdoor blocks are set back on every side, so two touching diagonally leave
                // a slit between their cores through which rays escape. Bridge it, visiting
                // only eastward joins so each bridge is emitted once.
                if (!east && !above) {
                    const northeast = blockBounds(x + 1, y - 1)
                    if (northeast) polyMap.push({
                        a: { x: right, y: top }, b: { x: northeast.left, y: northeast.bottom },
                    })
                }
                if (!east && !below) {
                    const southeast = blockBounds(x + 1, y + 1)
                    if (southeast) polyMap.push({
                        a: { x: right, y: bottom }, b: { x: southeast.left, y: southeast.top },
                    })
                }
                continue
            }
            const autoId = blockingAuto[y][x]
            if (autoId) {
                polyMap = polyMap.concat(evaluteAutotile(x, y, autoId))
            }
        }
    }

    return {
        // One straight wall arrives above as one face per tile it spans, and neighbouring faces
        // overlap. Joining them is free -- the join between two collinear faces is never a
        // silhouette corner -- and it is the difference between a few hundred occluders and a few
        // dozen, which every sightline then pays for twice over (one ray target, one hit test per
        // ray). Slots 0-3 are the view boundary, which callers overwrite by index.
        vectorLines: mergeCollinearSegments(polyMap, 4),
        autotileMap: blockingAuto,
    }
}

export const extractRegions = (
    roomId: string,
    json: any,
    regionClass: string[] = ["REGION"],
): Record<string, Region[]> => {
    const { layers } = json
    const objectLayer = layers.find(layer => layer.name === "objects")
    if (!objectLayer) {
        return {}
    }
    return objectLayer.objects
        .filter(object => regionClass.includes(object.class))
        .reduce((acc, meta) => {
            return {
                ...acc,
                [meta.name]: [
                    ...(acc[meta.name] || []),
                    {
                        locationId: meta.name,
                        roomId,
                        x: Math.floor(meta.x),
                        y: Math.floor(meta.y),
                        bounds: new Bounds(
                            Math.floor(meta.x),
                            Math.floor(meta.y),
                            Math.floor(meta.x) + meta.width,
                            Math.floor(meta.y) + meta.height,
                        ),
                        context: (meta.properties || []).reduce(
                            (acc, next) => ({
                                ...acc,
                                [next.name]: next.value,
                            }),
                            {},
                        ),
                    },
                ],
            }
        }, {})
}

export const extractBiomeRegions = (
    roomId: string,
    json: any,
    regionClass: string = "REGION",
): Record<string, Region[]> => {
    const regions = Object.values(extractRegions(roomId, json, ["BIOMEREGION"])).flat()
    return regions.reduce((acc, next) => {
        return {
            ...acc,
            [next.context.biome]: [...(acc[next.context.biome] || []), next],
        }
    }, {})
}
